import { api, getCsrf } from "../api";
import { saveOrDownloadFile } from "./download";
import { formatKathmanduWhen } from "./nepal-time";

export type RecordingExportSpec = {
  deviceId: string;
  channel: number;
  cameraLabel: string;
  deviceLabel: string;
  startAt: string;
  endAt: string;
};

export type RecordingExportJob = {
  jobId: string;
  status: "running" | "ready" | "error";
  filename: string;
  durationSec: number;
  startAt: string;
  endAt: string;
  channel: number;
  progressSec: number;
  bytesReceived?: number;
  expectedBytes?: number;
  percent?: number;
  error?: string;
};

/** One bar for the whole recording. Later polls and the final file save must not rewind it. */
export function mergeDownloadProgress(prev: DownloadProgress | null, next: DownloadProgress): DownloadProgress {
  if (!prev) return next;
  const percent = Math.max(prev.percent, next.percent);
  const receivedBytes = Math.max(prev.receivedBytes, next.receivedBytes);
  const totalBytes =
    prev.totalBytes != null && next.totalBytes != null
      ? Math.max(prev.totalBytes, next.totalBytes)
      : (next.totalBytes ?? prev.totalBytes);
  return {
    phase: "download",
    percent,
    receivedBytes,
    totalBytes,
    speedBps: next.speedBps ?? prev.speedBps,
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function recordingShareText(spec: RecordingExportSpec, durationSec: number): string {
  const start = new Date(spec.startAt);
  const when = Number.isNaN(start.getTime()) ? spec.startAt : formatKathmanduWhen(spec.startAt);
  const mins = Math.floor(durationSec / 60);
  const secs = durationSec % 60;
  const dur = mins > 0 ? `${mins}m ${String(secs).padStart(2, "0")}s` : `${secs}s`;
  return `${spec.cameraLabel} · ${spec.deviceLabel} · ${when} · ${dur}`;
}

export function exportFileUrl(deviceId: string, jobId: string): string {
  return `/api/devices/${encodeURIComponent(deviceId)}/recordings/export/${encodeURIComponent(jobId)}/file`;
}

export async function startRecordingExport(spec: RecordingExportSpec): Promise<RecordingExportJob> {
  return api<RecordingExportJob>(`/api/devices/${encodeURIComponent(spec.deviceId)}/recordings/export`, {
    method: "POST",
    body: JSON.stringify({
      channel: spec.channel,
      startAt: spec.startAt,
      endAt: spec.endAt,
      cameraLabel: spec.cameraLabel,
      deviceLabel: spec.deviceLabel,
    }),
  });
}

export async function pollRecordingExport(deviceId: string, jobId: string): Promise<RecordingExportJob> {
  return api<RecordingExportJob>(
    `/api/devices/${encodeURIComponent(deviceId)}/recordings/export/${encodeURIComponent(jobId)}`,
  );
}

export async function waitForRecordingExport(
  spec: RecordingExportSpec,
  onProgress?: (job: RecordingExportJob) => void,
): Promise<RecordingExportJob> {
  let job = await startRecordingExport(spec);
  onProgress?.(job);
  const deadline = Date.now() + Math.max(30_000, job.durationSec * 1000 + 90_000);
  while (job.status === "running") {
    if (Date.now() > deadline) throw new Error("Preparing this recording timed out.");
    await sleep(1000);
    job = await pollRecordingExport(spec.deviceId, job.jobId);
    onProgress?.(job);
  }
  if (job.status !== "ready") {
    throw new Error(job.error || "Could not prepare this recording.");
  }
  return job;
}

export type DownloadProgress = {
  phase: "download";
  percent: number;
  receivedBytes: number;
  totalBytes: number | null;
  speedBps: number | null;
};

async function fetchExportBlobWithProgress(
  deviceId: string,
  jobId: string,
  onProgress?: (received: number, total: number | null, speedBps: number | null) => void,
): Promise<Blob> {
  const headers = new Headers();
  const csrf = getCsrf();
  if (csrf) headers.set("X-CSRF-Token", csrf);
  const res = await fetch(exportFileUrl(deviceId, jobId), { credentials: "include", headers });
  if (!res.ok) {
    throw new Error(res.status === 401 ? "Sign in to download this recording." : `Download failed (${res.status})`);
  }
  const totalHeader = Number(res.headers.get("Content-Length") || 0);
  const total = Number.isFinite(totalHeader) && totalHeader > 0 ? totalHeader : null;
  if (!res.body) {
    const blob = await res.blob();
    onProgress?.(blob.size, blob.size, null);
    return blob;
  }
  const reader = res.body.getReader();
  const chunks: BlobPart[] = [];
  let received = 0;
  const started = Date.now();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value.slice());
      received += value.byteLength;
      const elapsed = Math.max(1, Date.now() - started) / 1000;
      onProgress?.(received, total, received / elapsed);
    }
  }
  onProgress?.(received, total ?? received, null);
  const type = res.headers.get("Content-Type") || "video/mp4";
  return new Blob(chunks, { type });
}

export async function downloadRecording(
  spec: RecordingExportSpec,
  onProgress?: (progress: DownloadProgress) => void,
): Promise<void> {
  let lastBytes = 0;
  let lastAt = Date.now();
  // A holder, not a `let`, so control-flow analysis does not freeze this as `null`
  // after the awaits below (`shown?.totalBytes` then errors as `never`).
  const shown: { current: DownloadProgress | null } = { current: null };
  const report = (next: DownloadProgress) => {
    shown.current = mergeDownloadProgress(shown.current, next);
    onProgress?.(shown.current);
  };
  const job = await waitForRecordingExport(spec, (row) => {
    const now = Date.now();
    const bytes = row.bytesReceived ?? 0;
    const dt = Math.max(1, now - lastAt) / 1000;
    const speed = bytes > lastBytes ? (bytes - lastBytes) / dt : null;
    lastBytes = bytes;
    lastAt = now;
    const percent = row.percent ?? (row.durationSec > 0 ? Math.round((row.progressSec / row.durationSec) * 100) : 0);
    const totalBytes = row.expectedBytes && row.expectedBytes > 0 ? row.expectedBytes : null;
    report({
      phase: "download",
      percent: row.status === "ready" ? 99 : Math.min(99, percent),
      receivedBytes: bytes,
      totalBytes,
      speedBps: speed,
    });
  });
  const blob = await fetchExportBlobWithProgress(spec.deviceId, job.jobId, (_received, _total, speedBps) => {
    report({
      phase: "download",
      percent: 99,
      receivedBytes: shown.current?.receivedBytes ?? job.bytesReceived ?? 0,
      totalBytes: shown.current?.totalBytes ?? job.expectedBytes ?? null,
      speedBps,
    });
  });
  const typed = blob.type && blob.type !== "application/octet-stream" ? blob : new Blob([blob], { type: "video/mp4" });
  await saveOrDownloadFile(job.filename, typed);
  report({
    phase: "download",
    percent: 100,
    receivedBytes: shown.current?.totalBytes ?? shown.current?.receivedBytes ?? blob.size,
    totalBytes: shown.current?.totalBytes ?? blob.size,
    speedBps: null,
  });
}

export function recordingShareUrl(spec: RecordingExportSpec): string {
  const href = globalThis.window?.location?.href ?? "https://localhost/playback";
  const url = new URL(href);
  url.searchParams.set("device", spec.deviceId);
  url.searchParams.set("from", spec.startAt);
  url.searchParams.set("to", spec.endAt);
  url.searchParams.set("channel", String(spec.channel));
  return url.href;
}

function flutterHandler(): { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> } | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as unknown as { flutter_inappwebview?: { callHandler?: (name: string, ...args: unknown[]) => Promise<unknown> } })
    .flutter_inappwebview;
  return bridge?.callHandler ? { callHandler: bridge.callHandler.bind(bridge) } : null;
}

export async function shareRecording(spec: RecordingExportSpec): Promise<{ shared: boolean; copied: boolean }> {
  const title = spec.cameraLabel;
  const url = recordingShareUrl(spec);
  const text = `${spec.cameraLabel} · ${spec.deviceLabel}`;
  const native = flutterHandler();
  if (native) {
    try {
      const result = (await native.callHandler("shareLink", {
        title,
        text,
        url,
      })) as { ok?: boolean; shared?: boolean; error?: string } | null;
      if (result?.ok) return { shared: result.shared !== false, copied: false };
    } catch {
      /* fall through to the browser share sheet */
    }
  }
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share === "function") {
    try {
      await nav.share({ title, text, url });
      return { shared: true, copied: false };
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return { shared: false, copied: false };
    }
  }
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return { shared: false, copied: true };
  }
  throw new Error("Sharing is not available in this browser. Copy the page address instead.");
}
